import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import { toast } from 'react-toastify';

const EmailLogs = () => {
  const [logs, setLogs] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 50; // Fixed page size
  
  const [filters, setFilters] = useState({
    email_type: '',
    status: '',
    user_email: '',
    from_date: '',
    to_date: '',
    limit: pageSize,
    offset: 0
  });

  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    fetchEmailAccounts();
  }, []);

  const fetchEmailAccounts = async () => {
    try {
      const response = await apiService.getEmailAccounts();
      const data = response?.data?.data?.users || response?.data?.users || response?.data || [];
      
      // Backend now provides clean emails, just use them directly
      const cleanedAccounts = data.map(account => ({
        ...account,
        email: (account.email || '').trim()
      })).filter(account => account.email); // Remove any empty emails
      
      console.log('✅ Loaded email accounts:', cleanedAccounts.length, cleanedAccounts);
      setAccounts(cleanedAccounts);
    } catch (error) {
      console.error('Failed to load email accounts:', error);
      toast.error('Failed to load email accounts');
    }
  };

  const fetchLogs = async () => {
    try {
      setLoading(true);
      console.log('🔍 Fetching logs with filters:', filters); // Debug what we're sending
      const response = await apiService.getEmailLogs(filters);
      console.log('📧 Email logs response:', response); // Debug log
      if (response && response.data) {
        // Handle the new API response structure
        let responseData;
        if (response.data.success && response.data.data) {
          responseData = response.data.data;
        } else {
          responseData = response.data;
        }
        
        // API returns activities in response.data.activities
        const emailLogs = responseData.activities || responseData.logs || [];
        const total = responseData.total_count || responseData.total || emailLogs.length;
        
        // Map API fields to frontend expected fields
        const mappedLogs = emailLogs.map(log => ({
          ...log,
          sender: log.user_email || log.sender || 'N/A',
          sent_at: log.timestamp || log.sent_at || null
        }));
        
        console.log('✅ Email logs found:', mappedLogs.length, 'of', total); // Debug log
        setLogs(mappedLogs);
        setTotalCount(total);
      }
    } catch (error) {
      console.error('Failed to load email logs:', error);
      toast.error('Failed to load email logs');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      sent: 'status-success',
      delivered: 'status-success',
      pending: 'status-warning',
      failed: 'status-error',
      bounced: 'status-error'
    };
    return `status-badge ${statusClasses[status] || 'bg-gray-100 text-gray-800'}`;
  };

  // Pagination functions
  const totalPages = Math.ceil(totalCount / pageSize);
  
  const goToPage = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
    setFilters(prev => ({ ...prev, offset: (page - 1) * pageSize }));
  };

  const nextPage = () => {
    if (currentPage < totalPages) {
      goToPage(currentPage + 1);
    }
  };

  const previousPage = () => {
    if (currentPage > 1) {
      goToPage(currentPage - 1);
    }
  };

  const handleDateRangeChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value, offset: 0 }));
    setCurrentPage(1);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value, offset: 0 }));
    setCurrentPage(1);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner mr-2"></div>
        <span>Loading email logs...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in pt-20">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Email Logs</h1>
          <p className="text-gray-500">Monitor email sending activity and performance</p>
        </div>
        <button onClick={() => fetchLogs()} className="btn-secondary">
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg p-4 card-shadow">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email Type</label>
            <select
              value={filters.email_type}
              onChange={(e) => handleFilterChange('email_type', e.target.value)}
              className="form-select"
            >
              <option value="">All Types</option>
              <option value="warmup">Warmup</option>
              <option value="marketing">Marketing</option>
              <option value="reply">Reply</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="form-select"
            >
              <option value="">All Statuses</option>
              <option value="sent">Sent</option>
              <option value="delivered">Delivered</option>
              <option value="pending">Pending</option>
              <option value="failed">Failed</option>
              <option value="bounced">Bounced</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Sender Email</label>
            <select
              value={filters.user_email}
              onChange={(e) => {
                const selectedEmail = e.target.value.trim();
                console.log('🔍 Sender email filter selected:', selectedEmail);
                handleFilterChange('user_email', selectedEmail);
              }}
              className="form-select"
            >
              <option value="">All Emails</option>
              {accounts
                .filter((account) => account.email && account.email.trim())
                .map((account) => {
                  const email = account.email.trim();
                  return (
                  <option key={email} value={email}>
                    {email}
                  </option>
                  );
                })}
            </select>
          </div>
        </div>
        
        {/* Date Range Filter */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-gray-200">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              📅 From Date
            </label>
            <input
              type="date"
              value={filters.from_date}
              onChange={(e) => handleDateRangeChange('from_date', e.target.value)}
              className="form-select"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              📅 To Date
            </label>
            <input
              type="date"
              value={filters.to_date}
              onChange={(e) => handleDateRangeChange('to_date', e.target.value)}
              className="form-select"
            />
          </div>
        </div>
      </div>

      {/* Logs Table */}
      <div className="bg-white rounded-lg card-shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Recent Email Activity</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subject</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sender</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Recipient</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sent At</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {logs.map((log) => (
                <tr key={log.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900 truncate max-w-xs">
                      {log.subject || 'No subject'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {log.email_type}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={getStatusBadge(log.status)}>
                      {log.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 truncate max-w-xs">
                    {log.sender || 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 truncate max-w-xs">
                    {log.recipient || 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {log.sent_at ? new Date(log.sent_at).toLocaleString() : 'Not sent'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {logs.length === 0 && (
          <div className="text-center py-12">
            <svg className="w-12 h-12 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No email logs found</h3>
            <p className="text-gray-500">Email logs will appear here once you start sending emails</p>
          </div>
        )}

        {/* Pagination Controls */}
        {logs.length > 0 && totalCount > pageSize && (
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Showing <span className="font-medium">{(currentPage - 1) * pageSize + 1}</span> to{' '}
              <span className="font-medium">{Math.min(currentPage * pageSize, totalCount)}</span> of{' '}
              <span className="font-medium">{totalCount}</span> results
            </div>
            
            <div className="flex items-center space-x-2">
              {/* Previous Button */}
              <button
                onClick={previousPage}
                disabled={currentPage === 1}
                className={`px-3 py-2 rounded-md text-sm font-medium ${
                  currentPage === 1
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                }`}
              >
                Previous
              </button>

              {/* Page Numbers */}
              <div className="flex items-center space-x-1">
                {[...Array(Math.min(5, totalPages))].map((_, idx) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = idx + 1;
                  } else if (currentPage <= 3) {
                    pageNum = idx + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + idx;
                  } else {
                    pageNum = currentPage - 2 + idx;
                  }

                  if (pageNum < 1 || pageNum > totalPages) return null;

                  return (
                    <button
                      key={pageNum}
                      onClick={() => goToPage(pageNum)}
                      className={`px-3 py-2 rounded-md text-sm font-medium ${
                        currentPage === pageNum
                          ? 'bg-blue-600 text-white'
                          : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                
                {totalPages > 5 && currentPage < totalPages - 2 && (
                  <>
                    <span className="px-2 text-gray-500">...</span>
                    <button
                      onClick={() => goToPage(totalPages)}
                      className="px-3 py-2 rounded-md text-sm font-medium bg-white text-gray-700 hover:bg-gray-50 border border-gray-300"
                    >
                      {totalPages}
                    </button>
                  </>
                )}
              </div>

              {/* Next Button */}
              <button
                onClick={nextPage}
                disabled={currentPage === totalPages}
                className={`px-3 py-2 rounded-md text-sm font-medium ${
                  currentPage === totalPages
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                }`}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailLogs;
